import { useMemo } from 'react'
import { Badge, Box, Code, HStack, IconButton, Text, Tooltip } from '@chakra-ui/react'
import { FiCopy, FiExternalLink } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import type { ColumnConfig } from '@/components/common/DataTable/types'
import { ChainBadge } from '@/components/common/ChainBadge'
import { useCopyableCodeStyles } from '@/components/common/useCopyableCodeStyles'
import type { ContractDeploymentRecord } from '@/types'
import { getExplorerAddressUrl, getExplorerTxUrl } from '@/constants/chains'
import { formatDateTime } from '@/utils/formatUtils'

interface UseDeploymentTableColumnsOptions {
  secondaryTextColor: string
  copyToClipboard: (text: string) => void
  formatGasCost: (record: ContractDeploymentRecord) => string
  getGasCostDisplayParts: (
    record: ContractDeploymentRecord
  ) => { gasLine: string | null; usdLine: string | null }
  formatSettlementPreviewDisplay: (value?: boolean) => string
  formatTerminalCleanupState: (record: ContractDeploymentRecord) => string
  getDeploymentDisplayAddress: (record: ContractDeploymentRecord) => string | undefined
  getDeploymentStatusColor: (record: ContractDeploymentRecord) => string
  getInitializationStatusColor: (isInitialized?: boolean) => string
  getInitializationStatusDisplay: (isInitialized?: boolean) => string
  getVerificationStatusColor: (status?: string) => string
  getVerificationStatusText: (status?: string) => string
  getSettlementPreviewColor: (value?: boolean) => string
  resolveDeploymentChainId: (record?: ContractDeploymentRecord | null) => string
  splitOrderId: (id?: string | null) => string[]
  truncateHash: (hash: string) => string
}

export const useDeploymentTableColumns = ({
  secondaryTextColor,
  copyToClipboard,
  formatGasCost,
  getGasCostDisplayParts,
  formatSettlementPreviewDisplay,
  formatTerminalCleanupState,
  getDeploymentDisplayAddress,
  getDeploymentStatusColor,
  getInitializationStatusColor,
  getInitializationStatusDisplay,
  getVerificationStatusColor,
  getVerificationStatusText,
  getSettlementPreviewColor,
  resolveDeploymentChainId,
  splitOrderId,
  truncateHash,
}: UseDeploymentTableColumnsOptions): ColumnConfig<ContractDeploymentRecord>[] => {
  const { t } = useTranslation()
  const copyableCodeStyles = useCopyableCodeStyles()

  return useMemo(
    () => [
      {
        key: 'orderId',
        title: (
          <Text whiteSpace="normal" lineHeight="1.2">
            {t('contractRegistry.table.orderId')}
          </Text>
        ),
        width: '16%',
        minW: '200px',
        sortable: true,
        render: (_, record) => {
          const primaryId = record.orderId || record.order_id || ''
          const primaryLines = splitOrderId(primaryId)
          const secondaryId =
            record.orderId && record.order_id && record.orderId !== record.order_id
              ? record.order_id
              : null

          return (
            <Box
              minH="44px"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-start"
              gap={secondaryId ? 1 : 0}
            >
              <Tooltip label={primaryId ? t('common.copy') : ''}>
                <Code
                  as="div"
                  bg="transparent"
                  fontFamily="mono"
                  fontSize="10px"
                  lineHeight="1.2"
                  whiteSpace="pre-line"
                  wordBreak="break-word"
                  textShadow="none"
                  title={primaryId}
                  cursor={primaryId ? 'pointer' : 'default'}
                  _hover={primaryId ? copyableCodeStyles.hover : undefined}
                  _active={primaryId ? copyableCodeStyles.active : undefined}
                  onClick={() => primaryId && copyToClipboard(primaryId)}
                >
                  {primaryLines.length > 0 ? primaryLines.join('\n') : t('common.unknown')}
                </Code>
              </Tooltip>
              {secondaryId && (
                <Text fontSize="xs" color={secondaryTextColor} whiteSpace="nowrap" title={secondaryId}>
                  {secondaryId}
                </Text>
              )}
            </Box>
          )
        },
      },
      {
        key: 'deployment_attempt',
        title: t('contractRegistry.deployment.table.attempt'),
        width: '6%',
        sortable: true,
        render: (_, record) => (
          <Badge colorScheme="blue" size="sm">
            #{record.deployment_attempt || 1}
          </Badge>
        ),
      },
      {
        key: 'status_id',
        title: t('contractRegistry.table.status'),
        width: '7%',
        sortable: true,
        render: (_, record) => (
          <Badge colorScheme={getDeploymentStatusColor(record)} size="sm" fontSize="10px">
            {record.status_name || record.status_id}
          </Badge>
        ),
      },
      {
        key: 'chain_id',
        title: t('contractRegistry.deployment.table.chain'),
        width: '7%',
        sortable: true,
        render: (_, record) => (
          <ChainBadge
            chainId={resolveDeploymentChainId(record) || 'unknown'}
            chainCode={record.chain_code ?? record.chainCode}
            chainName={record.chain_display_name ?? record.chainDisplayName ?? record.chain_name}
            chainNumericId={record.chain_numeric_id ?? record.chainNumericId}
            fontSize="10px"
            px={1.5}
            py={0.5}
          />
        ),
      },
      {
        key: 'address_verification_status_id',
        title: t('contractRegistry.deployment.table.verification'),
        width: '8%',
        sortable: true,
        render: value => (
          <Badge colorScheme={getVerificationStatusColor(value)} size="sm" fontSize="10px">
            {getVerificationStatusText(value)}
          </Badge>
        ),
      },
      {
        key: 'is_initialized',
        title: t('contractRegistry.deployment.table.initialization'),
        width: '8%',
        sortable: true,
        render: (_, record) => (
          <Badge
            colorScheme={getInitializationStatusColor(record.is_initialized)}
            size="sm"
            fontSize="10px"
          >
            <Text as="span" fontSize="10px" lineHeight="1.15" whiteSpace="pre-line" textAlign="center">
              {getInitializationStatusDisplay(record.is_initialized)}
            </Text>
          </Badge>
        ),
      },
      {
        key: 'terminal_cleanup_state',
        title: (
          <Text whiteSpace="normal" lineHeight="1.2">
            {t('contractRegistry.deployment.table.terminalCleanup.title')}
          </Text>
        ),
        width: '10%',
        render: (_, record) => (
          <Text
            fontSize="10px"
            lineHeight="1.2"
            whiteSpace="normal"
            overflowWrap="anywhere"
            wordBreak="break-word"
          >
            {formatTerminalCleanupState(record)}
          </Text>
        ),
      },
      {
        key: 'payment_address',
        title: (
          <Text whiteSpace="normal" lineHeight="1.2">
            {t('contractRegistry.table.paymentAddress', 'Payment Address')}
          </Text>
        ),
        width: '16%',
        render: (_, record) => {
          const deploymentAddress = getDeploymentDisplayAddress(record) || ''

          return deploymentAddress ? (
            <HStack spacing={1} maxW="100%">
              <Code fontSize="xs" flex="1" title={deploymentAddress}>
                {truncateHash(deploymentAddress)}
              </Code>
              <Tooltip label="Copy Address">
                <IconButton
                  aria-label="Copy"
                  icon={<FiCopy />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(deploymentAddress)}
                />
              </Tooltip>
              <Tooltip label="View on Explorer">
                <IconButton
                  aria-label="Explorer"
                  icon={<FiExternalLink />}
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    const chainId = resolveDeploymentChainId(record) || 'unknown'
                    const url = getExplorerAddressUrl(chainId, deploymentAddress)
                    if (url) window.open(url, '_blank')
                  }}
                />
              </Tooltip>
            </HStack>
          ) : (
            <Text fontSize="xs" color={secondaryTextColor}>
              {t('common.na')}
            </Text>
          )
        },
      },
      {
        key: 'deployment_tx_hash',
        title: t('contractRegistry.deployment.table.txHash'),
        width: '16%',
        render: (_, record) =>
          record.deployment_tx_hash ? (
            <HStack spacing={1} maxW="100%">
              <Code fontSize="xs" flex="1" title={record.deployment_tx_hash}>
                {truncateHash(record.deployment_tx_hash)}
              </Code>
              <Tooltip label="Copy TX Hash">
                <IconButton
                  aria-label="Copy"
                  icon={<FiCopy />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(record.deployment_tx_hash!)}
                />
              </Tooltip>
              <Tooltip label="View on Explorer">
                <IconButton
                  aria-label="Explorer"
                  icon={<FiExternalLink />}
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    const chainId = resolveDeploymentChainId(record) || 'unknown'
                    const url = getExplorerTxUrl(chainId, record.deployment_tx_hash!)
                    if (url) window.open(url, '_blank')
                  }}
                />
              </Tooltip>
            </HStack>
          ) : (
            <Text fontSize="xs" color={secondaryTextColor}>
              -
            </Text>
          ),
      },
      {
        key: 'gas_used',
        title: t('contractRegistry.deployment.table.gasCost'),
        width: '8%',
        render: (_, record) => {
          const { gasLine, usdLine } = getGasCostDisplayParts(record)
          const fullText = formatGasCost(record)

          if (!gasLine && !usdLine) {
            return <Text fontSize="xs">-</Text>
          }

          return (
            <Box title={fullText}>
              {gasLine && (
                <Text fontSize="xs" lineHeight="1.2" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                  {gasLine}
                </Text>
              )}
              {usdLine && (
                <Text fontSize="10px" lineHeight="1.2" color={secondaryTextColor} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                  {usdLine}
                </Text>
              )}
            </Box>
          )
        },
      },
      {
        key: 'deployed_at',
        title: t('contractRegistry.deployment.table.deployedAt'),
        width: '8%',
        sortable: true,
        render: value => (
          <Text fontSize="10px">
            {value ? formatDateTime(value, { fallback: '-', timeStyle: 'medium' }) : '-'}
          </Text>
        ),
      },
      {
        key: 'is_pass_settlement_preview',
        title: t('contractRegistry.deployment.table.settlementPreview'),
        width: '9%',
        render: (_, record) => (
          <Badge
            colorScheme={getSettlementPreviewColor(record.is_pass_settlement_preview)}
            size="sm"
            fontSize="10px"
          >
            <Text as="span" fontSize="10px" lineHeight="1.15" whiteSpace="pre-line" textAlign="center">
              {formatSettlementPreviewDisplay(record.is_pass_settlement_preview)}
            </Text>
          </Badge>
        ),
      },
    ],
    [
      copyToClipboard,
      copyableCodeStyles.active,
      copyableCodeStyles.hover,
      formatGasCost,
      formatSettlementPreviewDisplay,
      formatTerminalCleanupState,
      getDeploymentDisplayAddress,
      getDeploymentStatusColor,
      getGasCostDisplayParts,
      getInitializationStatusColor,
      getInitializationStatusDisplay,
      getSettlementPreviewColor,
      getVerificationStatusColor,
      getVerificationStatusText,
      resolveDeploymentChainId,
      secondaryTextColor,
      splitOrderId,
      t,
      truncateHash,
    ]
  )
}
